import CloseIcon from '@assets/images/common/icon/close.svg';
import ToolTipIcon from '@assets/images/common/icon/tooltip/tooltip.svg';
import { AnonymousPostEnum } from '@features/shared/community/enums/anonymousPost';
import {
  Channel,
  ChannelForm,
  ChannelUpdateErrorResponse,
} from '@src/api/shared/community/channelApiClient';
import EmojiBottomMenuSp from '@src/features/shared/community/components/chat/EmojiBottomMenuSp';
import EmojiDropdownMenuPc from '@src/features/shared/community/components/chat/EmojiDropdownMenuPc';
import { ChannelTypeEnum } from '@src/features/shared/community/enums/channelType';
import { ChannelVisibilityEnum } from '@src/features/shared/community/enums/channelVisibility';
import { FileAttachmentEnum } from '@src/features/shared/community/enums/fileAttachment';
import { PostPermissionEnum } from '@src/features/shared/community/enums/postPermission';
import useIsMobile from '@src/hooks/useIsMobile';
import { useScrollToError } from '@src/hooks/useScrollToError';
import Modal from '@src/layouts/Modal';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import Checkbox from '@src/ui/elements/form/Checkbox';
import Input from '@src/ui/elements/form/Input';
import Select from '@src/ui/elements/form/Select';
import Textarea from '@src/ui/elements/form/Textarea';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import { Emoji } from 'emoji-picker-react';
import React, { useEffect, useRef, useState } from 'react';

interface ChannelSettingModalProps {
  isOpen: boolean;
  channel?: Channel;
  errors?: ChannelUpdateErrorResponse;
  onSave: (formData: ChannelForm) => void;
  onClose: () => void;
}

const CHANNEL_SELECTOR = {
  visibility_selector: ChannelVisibilityEnum,
  post_permission_selector: PostPermissionEnum,
  type_selector: ChannelTypeEnum,
  allow_file_attachment_selector: FileAttachmentEnum,
  allow_anonymous_post: AnonymousPostEnum,
};

const DEFAULT_CHANNEL: ChannelForm = {
  code: '',
  name: '',
  description: '',
  visibility: null,
  allow_file_attachment: null,
  allow_anonymous_post: null,
  email_notification: '0',
  in_app_notification: '1',
  type: '1',
  post_permission: null,
  pinned_chat: null,
  is_access_denied: true,
  can_post: true,
  emoji: '',
  unread_count: 0,
  mention_count: '',
  has_unread: false,
  has_mention: false,
  first_unread_chat_id: null,
  sort_order: 0,
};

const CreateEditChannelModal: React.FC<ChannelSettingModalProps> = ({
  isOpen,
  channel,
  onSave,
  onClose,
  errors,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [activeHelpId, setActiveHelpId] = useState<number | null>(null);
  const helpRef = useRef(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [emojiButtonPosition, setEmojiButtonPosition] = useState({ x: 0, y: 0 });
  const isMobile = useIsMobile();

  const handleMouseOver = (itemId: number): void => {
    setActiveHelpId(itemId);
  };
  const handleMouseLeave = (): void => {
    setActiveHelpId(null);
  };
  useEffect(() => {
    const handleOutsideClick = () => {
      setActiveHelpId(null);
    };
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const [formData, setFormData] = useState<ChannelForm>(channel ?? DEFAULT_CHANNEL);

  useEffect(() => {
    setFormData(channel ?? DEFAULT_CHANNEL);
  }, [isOpen, channel]);

  const updateEmojiButtonPosition = () => {
    if (emojiButtonRef.current && modalContentRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect();
      const modalRect = modalContentRef.current.getBoundingClientRect();

      setEmojiButtonPosition({
        x: rect.left - modalRect.left,
        y: rect.bottom - modalRect.top + modalContentRef.current.scrollTop,
      });
    }
  };

  useEffect(() => {
    if (isOpen && emojiButtonRef.current) {
      updateEmojiButtonPosition();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showEmojiPicker || !modalContentRef.current) {
      return;
    }

    const handleScroll = () => {
      updateEmojiButtonPosition();
    };

    const modalElement = modalContentRef.current;
    modalElement.addEventListener('scroll', handleScroll);
    return () => {
      modalElement.removeEventListener('scroll', handleScroll);
    };
  }, [showEmojiPicker]);

  const handleChange = (key: keyof ChannelForm, value: string | number | null) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const fieldRefs = useScrollToError(errors ?? {});

  if (!isOpen || formData === null) {
    return null;
  }

  return (
    <Modal
      ref={modalContentRef}
      width='800px'
    >
      <div className='flex grow flex-col gap-4 p-6'>
        <div className='border-b border-b-gray-350 pb-6 md:pb-[14px]'>
          <h2 className='text-xl font-semibold leading-loose'>
            {channel === undefined ? 'チャンネルの作成' : 'チャンネルの設定変更'}
          </h2>
        </div>
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-[10px]'>
            <div className='flex items-center gap-3 md:gap-2'>
              <label className='font-medium'>チャンネル名</label>
              <div className='grid h-[15px] w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                必須
              </div>
            </div>
            <div
              className='flex w-full flex-col gap-[10px]'
              ref={(el) => (fieldRefs.current['channel.name'] = el)}
            >
              <Input
                size='m'
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder='例）DX事例共有チャンネル'
                error={hasError(errors?.channel.name)}
              />
              <ErrorMessage>{firstError(errors?.channel.name)}</ErrorMessage>
            </div>
          </div>
          <div className='flex flex-col gap-[10px]'>
            <div className='flex items-center gap-3 md:gap-2'>
              <label className='font-medium'>チャンネルの絵文字</label>
            </div>
            <div>
              <div className='flex w-full flex-col gap-[10px]'>
                <div className='flex items-center gap-2 whitespace-nowrap'>
                  <button
                    ref={emojiButtonRef}
                    type='button'
                    className='flex h-[50px] w-[240px] items-center rounded-[2px] border border-solid border-gray-250 bg-down bg-[center_right_12px] bg-no-repeat pl-4 pr-7 text-[20px] lg:w-full'
                    onClick={() => {
                      updateEmojiButtonPosition();
                      setShowEmojiPicker(!showEmojiPicker);
                    }}
                  >
                    {formData.emoji ? (
                      <Emoji
                        size={20}
                        unified={formData.emoji}
                      />
                    ) : (
                      <span className='text-sm text-gray-400'>絵文字を選択</span>
                    )}
                  </button>
                  {formData.emoji && (
                    <button
                      type='button'
                      className='text-xs text-primary-500 underline hover:no-underline'
                      onClick={() => handleChange('emoji', '')}
                    >
                      削除
                    </button>
                  )}
                </div>
              </div>
              <ErrorMessage>{firstError(errors?.channel.emoji)}</ErrorMessage>
              {showEmojiPicker && (
                <>
                  {!isMobile && (
                    <EmojiDropdownMenuPc
                      onEmojiClick={(emojiObject) => {
                        handleChange('emoji', emojiObject.unified);
                        setShowEmojiPicker(false);
                      }}
                      position={emojiButtonPosition}
                      menuPositionBelow={true}
                      horizontalAlign='left'
                      isInsideModal={true}
                    />
                  )}

                  {isMobile && (
                    <EmojiBottomMenuSp
                      handleCloseClick={() => setShowEmojiPicker(false)}
                      handleEmojiClick={(emojiObject) => {
                        handleChange('emoji', emojiObject.unified);
                        setShowEmojiPicker(false);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <div className='flex flex-col gap-[10px]'>
            <div className='flex items-center gap-3 md:gap-2'>
              <label className='font-medium'>チャンネル概要</label>
            </div>
            <div
              className='flex w-full flex-col gap-[8px] md:[&>textarea]:!h-[204px]'
              ref={(el) => (fieldRefs.current['channel.description'] = el)}
            >
              <Textarea
                height='101px'
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder='なにを話題にするチャンネルなのかを参加者に示します。チャンネル名の横に表示されます。
例）自社のDX（デジタルトランスフォーメーション）事例を共有し、トレンドや成功事例について議論するチャンネルです。'
                resizable='vertical'
                error={hasError(errors?.channel.description)}
              />
              <ErrorMessage>{firstError(errors?.channel.description)}</ErrorMessage>
            </div>
          </div>
          <div className='flex gap-[14px] md:flex-col'>
            <div className='mb-3 hidden text-lg font-semibold md:block'>チャンネル設定</div>
            <div className='flex w-1/2 flex-col gap-[10px] md:w-full'>
              <label className='font-medium'>チャンネルタイプ</label>
              <Select
                size='m'
                items={Object.entries(CHANNEL_SELECTOR.type_selector).map(([key, value]) => ({
                  id: key,
                  name: value,
                }))}
                initialValue={formData.type}
                onSelect={(value) => handleChange('type', value)}
                error={hasError(errors?.channel.type)}
                disabled
              />
              <ErrorMessage>{firstError(errors?.channel.type)}</ErrorMessage>
            </div>
            <div className='relative flex w-1/2 flex-col gap-[10px] md:w-full'>
              <div className='flex items-center gap-3 md:gap-2'>
                <label className='font-medium'>公開設定</label>
                <div className='grid h-[15px] w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
                <div className='relative md:static'>
                  <button
                    type='button'
                    onMouseOver={() => handleMouseOver(1)}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={() => handleMouseOver(1)}
                    onTouchEnd={handleMouseLeave}
                  >
                    <img
                      src={ToolTipIcon}
                      alt=''
                      className=''
                    />
                  </button>
                  {activeHelpId === 1 && ( // ここで↑の数値を指定
                    <div
                      ref={helpRef} // これを表示されるメッセージの親につける
                      className='absolute left-1/2  top-0 z-10 -translate-x-1/2 -translate-y-full md:top-1/2 md:w-full md:-translate-y-1/2'
                    >
                      <div className='flex min-w-[460px] flex-col gap-3 rounded-md border border-gray-200 bg-white/90 p-3 md:-ml-3 md:w-[calc(100%+24px)] md:min-w-full'>
                        <div className='flex flex-col gap-1'>
                          <div className='text-xs font-semibold'>公開</div>
                          <p className='text-xs'>
                            チャンネル名・概要・会話内容は、すべてのユーザーが閲覧可能です。
                            <br></br>
                            また、すべてのログイン済みユーザーが、メッセージの投稿やリアクションを行うことができます。
                          </p>
                        </div>
                        <div className='flex flex-col gap-1'>
                          <div className='text-xs font-semibold'>限定公開</div>
                          <p className='text-xs'>
                            チャンネル名はすべてのユーザーに表示されますが、概要・会話内容は招待されたユーザーのみが閲覧可能です。
                            <br></br>
                            メッセージの投稿・リアクションも、招待されたユーザーに限られます。
                          </p>
                        </div>
                        <div className='flex flex-col gap-1'>
                          <div className='text-xs font-semibold'>非公開(プライベート)</div>
                          <p className='text-xs'>
                            招待されたユーザーのみが、チャンネル名・概要・会話内容の閲覧、メッセージの投稿・リアクションを行うことが可能です。
                            <br></br>
                            招待されていないユーザーには、チャンネル名が表示されず、チャンネルの存在が知られることはありません。
                          </p>
                        </div>
                        <div className='flex flex-col gap-1'>
                          <p className='text-xs'>
                            ※いずれの公開設定でも、「ユーザー投稿」設定で「テナント管理者のみ投稿可」に変更できます。
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['channel.visibility'] = el)}
              >
                <Select
                  size='m'
                  items={Object.entries(CHANNEL_SELECTOR.visibility_selector).map(
                    ([key, value]) => ({
                      id: key,
                      name: value,
                    }),
                  )}
                  initialValue={String(formData.visibility)}
                  onSelect={(value) => {
                    handleChange('visibility', value ? Number(value) : null);
                  }}
                  error={hasError(errors?.channel.visibility)}
                  className='bg-white'
                />
                <ErrorMessage>{firstError(errors?.channel.visibility)}</ErrorMessage>
              </div>
            </div>
          </div>
          <div className='flex gap-[14px] md:flex-col'>
            <div className='mb-3 mt-[6px] hidden text-lg font-semibold md:block'>投稿設定</div>
            <div className='flex w-1/2 flex-col gap-[10px] md:w-full'>
              <div className='flex items-center gap-3 md:gap-2'>
                <label className='font-medium'>ユーザー投稿</label>
                <div className='grid h-[15px] w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['channel.post_permission'] = el)}
              >
                <Select
                  size='m'
                  items={Object.entries(CHANNEL_SELECTOR.post_permission_selector).map(
                    ([key, value]) => ({
                      id: key,
                      name: value,
                    }),
                  )}
                  initialValue={formData.post_permission?.toString()}
                  onSelect={(value) =>
                    handleChange('post_permission', value ? Number(value) : null)
                  }
                  error={hasError(errors?.channel.post_permission)}
                  className='bg-white'
                />
                <ErrorMessage>{firstError(errors?.channel.post_permission)}</ErrorMessage>
              </div>
            </div>
            <div className='relative flex w-1/2 flex-col gap-[10px] md:w-full'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>投稿へのファイル添付</label>
                <div className='grid h-[15px] w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
                <div className='relative md:static'>
                  <button
                    type='button'
                    onMouseOver={() => handleMouseOver(2)}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={() => handleMouseOver(2)}
                    onTouchEnd={handleMouseLeave}
                  >
                    <img
                      src={ToolTipIcon}
                      alt=''
                      className=''
                    />
                  </button>
                  {activeHelpId === 2 && (
                    <div
                      ref={helpRef}
                      className='absolute left-1/2  top-0 z-10 -translate-x-1/2 -translate-y-full md:top-1/2 md:w-full md:-translate-y-1/2'
                    >
                      <div className='flex min-w-[280px] flex-col gap-3 rounded-md border border-gray-200 bg-white/90 p-3 md:-ml-3 md:w-[calc(100%+24px)] md:min-w-full'>
                        <div className='flex flex-col gap-1'>
                          <div className='text-xs font-semibold'>ファイル添付を許可しない</div>
                          <p className='text-xs'>
                            チャンネルへのファイル添付を不可にします。画像の添付もできません。
                          </p>
                        </div>
                        <div className='flex flex-col gap-1'>
                          <div className='text-xs font-semibold'>ファイル添付を許可する</div>
                          <p className='text-xs'>チャンネルへのファイル添付を可能にします。</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['channel.allow_file_attachment'] = el)}
              >
                <Select
                  size='m'
                  items={Object.entries(CHANNEL_SELECTOR.allow_file_attachment_selector).map(
                    ([key, value]) => ({
                      id: key,
                      name: value,
                    }),
                  )}
                  initialValue={channel ? String(formData.allow_file_attachment) : ''}
                  onSelect={(value) => {
                    handleChange('allow_file_attachment', value ? Number(value) : null);
                  }}
                  error={hasError(errors?.channel.allow_file_attachment)}
                  className='bg-white'
                />
                <ErrorMessage>{firstError(errors?.channel.allow_file_attachment)}</ErrorMessage>
              </div>
            </div>
          </div>
          <div className='flex gap-[14px] md:flex-col'>
            <div className='relative flex w-1/2 flex-col gap-[10px] md:w-full'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>匿名投稿</label>
                <div className='grid h-[15px] w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div
                className='flex w-full flex-col'
                ref={(el) => (fieldRefs.current['channel.allow_anonymous_post'] = el)}
              >
                <Select
                  size='m'
                  items={Object.entries(CHANNEL_SELECTOR.allow_anonymous_post).map(
                    ([key, value]) => ({
                      id: key,
                      name: value,
                    }),
                  )}
                  initialValue={channel ? String(formData.allow_anonymous_post) : ''}
                  onSelect={(value) => {
                    handleChange('allow_anonymous_post', value ? Number(value) : null);
                  }}
                  error={hasError(errors?.channel.allow_anonymous_post)}
                  className='bg-white'
                />
                <ErrorMessage>{firstError(errors?.channel.allow_anonymous_post)}</ErrorMessage>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-[10px]'>
            <div className='font-medium'>新規投稿の通知設定</div>
            <div className='flex gap-6'>
              {/* InApp通知実装時に復活 */}
              {/* <div className='flex items-center gap-[10px]'>
                <Checkbox
                  id='app-notification'
                  checked={formData.in_app_notification == '1'}
                  onChange={(e) =>
                    handleChange('in_app_notification', e.target.checked ? '1' : '0')
                  }
                />
                <label
                  htmlFor='app-notification'
                  className='cursor-pointer'
                >
                  InApp通知
                </label>
              </div> */}
              <div className='flex items-center gap-[10px]'>
                <Checkbox
                  id='email-notification'
                  checked={formData.email_notification == '1'}
                  onChange={(e) => handleChange('email_notification', e.target.checked ? '1' : '0')}
                />
                <label
                  htmlFor='email-notification'
                  className='cursor-pointer'
                >
                  メール通知
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className='mt-auto flex justify-center gap-8 md:px-[16px] md:py-[24px]'>
          <div className='w-[172px] md:h-[42px] md:w-[120px]'>
            <Button
              className='whitespace-nowrap md:text-sm'
              variant='secondary'
              fullRounded
              fullWidth
              outline
              onClick={() => {
                if (!channel) {
                  setFormData(DEFAULT_CHANNEL);
                }
                onClose();
              }}
            >
              キャンセル
            </Button>
          </div>
          <div className='w-[172px] md:h-[42px] md:w-[120px]'>
            <Button
              className='md:text-sm'
              fullRounded
              fullWidth
              outline
              onClick={() => onSave(formData)}
            >
              保存する
            </Button>
          </div>
        </div>
        <button
          className='absolute right-[8px] top-[8px] grid size-5 place-items-center rounded-full bg-gray-50'
          onClick={onClose}
        >
          <img
            src={CloseIcon}
            alt=''
            width={16}
            height={16}
          />
        </button>
      </div>
    </Modal>
  );
};

export default CreateEditChannelModal;
